import { type FC, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircularProgress } from "@privacy/ui";
import Box from "@mui/material/Box";
import { usePermission } from "../../shared/permissions";
import { systemsQueryOptions } from "../../api/systems";
import { SystemIcon3 } from "../../shared/icons";
import { CreateSystemDialog } from "../../pages/SystemsGridPage/CreateSystemDialog";
import type { InputProps } from "../DynamicInput";
import type { InlineInputStateStyles } from "../inlineForm/inputs/InlineInputState";
import { useGetSortByKey } from "../../shared/SortByKey";
import type { CreateNewDialogProps } from "./MultiAutocomplete";
import { MultiAutocomplete } from "./MultiAutocomplete";

export const SystemInput: FC<InputProps<string[]> & { inlineInputStateStyles?: InlineInputStateStyles }> = props => {
  const { data } = useQuery(systemsQueryOptions());
  const { hasPermission } = usePermission();
  const sortByKey = useGetSortByKey();
  const options = useMemo(
    () => sortByKey(data?.map(system => ({ label: system.name, value: system.id })) ?? [], "label"),
    [data, sortByKey]
  );

  if (!data)
    return (
      <Box sx={{ py: 1.5, pl: 2, width: "100%" }}>
        <CircularProgress size={16} />
      </Box>
    );

  return (
    <MultiAutocomplete
      {...props}
      options={options}
      inlineInputStateStyles={props.inlineInputStateStyles}
      renderIcon={iconProps => <SystemIcon3 {...iconProps} />}
      placeholderKey="DynamicForm.Inline.InputPlaceholder"
      chipVariant="entity"
      {...(hasPermission("nova-forms-api:system:create")
        ? { CreateNewDialog: CreateNewSystemDialog }
        : { noOptionsTextKey: "DynamicForm.Option.Empty" })}
    />
  );
};

const CreateNewSystemDialog: FC<CreateNewDialogProps> = ({ onClose, value, onCreated }) => (
  <CreateSystemDialog
    onClose={onClose}
    onCreated={system => onCreated({ value: system.id, label: system.name })}
    defaultName={value}
  />
);
